// Everything the analyst does with one identity's candidates — confirm, reject,
// the merge the dialog asks about first (ADR-0042, ADR-0052) and the reference
// toggles — and the refusal to show for the last of them.
import { useState } from 'react'
import type { CandidateActions } from '@/components/faces/CandidateDecision'
import type { MergeIdentitiesPair } from '@/components/faces/MergeIdentitiesDialog'
import type { FaceDecision, IdentityDetailQuery } from '@/services/api/face-matching'
import { decisionErrorKey, type FaceActionErrorKey, latestRefusal, mergeErrorKey } from '../errors'
import { confirmedMerge } from '../merge-pair'
import {
  inFlight,
  useFaceDecision,
  useFaceReferenceToggle,
  useIdentityMerge,
} from './useFaceMatching'

export interface CandidateDecisions {
  /** Confirm, reject, ask to merge — what the star, its panel and the table offer. */
  actions: CandidateActions
  /** The assigned candidate the merge dialog asks about; null while it is closed. */
  mergeFaceId: string | null
  /** Run the merge the dialog asked about, the way the analyst confirmed it. */
  confirmMerge: (confirmed: MergeIdentitiesPair) => void
  closeMerge: () => void
  toggleReference: (faceId: string, reference: boolean) => void
  togglingFaceId: string | null
  /**
   * The last action's refusal (see `FaceActionErrorKey`). Each time the cache
   * rolled back, so the analyst is told what happened.
   */
  failureKey: FaceActionErrorKey | null
}

/**
 * *queries* are every detail the open identity is read through — see
 * `useFaceDecision`. *onDecided* runs after every decision and merge that left
 * the open identity standing, on top of the queries the mutations refresh
 * themselves.
 */
export function useCandidateDecisions(
  caseId: string,
  personId: string | null,
  queries: IdentityDetailQuery[],
  onDecided?: () => void
): CandidateDecisions {
  const decision = useFaceDecision(caseId, personId, queries)
  const merge = useIdentityMerge(caseId, personId, queries)
  const referenceToggle = useFaceReferenceToggle(caseId, personId, queries)
  const [mergeFaceId, setMergeFaceId] = useState<string | null>(null)

  // After a decision the candidate leaves the table and the star, and the next
  // strongest takes its place (SC-007).
  const decide = (faceId: string, next: FaceDecision) =>
    decision.mutate({ faceId, decision: next }, { onSettled: () => onDecided?.() })

  return {
    actions: {
      onConfirm: faceId => decide(faceId, 'confirm'),
      onReject: faceId => decide(faceId, 'reject'),
      onMerge: setMergeFaceId,
      pendingFaceId: inFlight(decision) ?? inFlight(merge),
    },
    mergeFaceId,
    confirmMerge: pair => {
      if (!mergeFaceId || !personId) return
      const confirmed = confirmedMerge(pair)
      // An identity folded into the other one has nothing left to re-read:
      // whoever shows it follows the survivor instead (`useMergedInto`).
      const reread = (_detail: unknown, error: unknown) => {
        if (error || confirmed.survivorId === personId) onDecided?.()
      }
      merge.mutate({ personId, faceId: mergeFaceId, ...confirmed }, { onSettled: reread })
    },
    closeMerge: () => setMergeFaceId(null),
    toggleReference: (faceId, reference) => referenceToggle.mutate({ faceId, reference }),
    togglingFaceId: inFlight(referenceToggle),
    failureKey: latestRefusal([
      { at: decision.submittedAt, key: decisionErrorKey(decision.error) },
      { at: referenceToggle.submittedAt, key: decisionErrorKey(referenceToggle.error) },
      { at: merge.submittedAt, key: mergeErrorKey(merge.error) },
    ]),
  }
}
